import { useState, useRef, useEffect, useMemo } from 'react';
import { Check, ChevronsUpDown, Search } from 'lucide-react';
import { cn } from '../lib/utils';

export function SearchableSelect({ options, value, onValueChange, placeholder = 'Select...', searchPlaceholder = 'Search...', className, triggerClassName, testId }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const selectedOption = options.find(o => o.value === value);

  const filtered = useMemo(() => {
    if (!search) return options;
    const term = search.toLowerCase();
    return options.filter(o => {
      if (o.isHeader) return false;
      return o.label.toLowerCase().includes(term);
    });
  }, [options, search]);

  // Group headers: when not searching, render headers; when searching, flatten
  const displayItems = useMemo(() => {
    if (search) return filtered;
    const items = [];
    let lastGroup = null;
    for (const opt of filtered) {
      if (opt.isHeader) {
        items.push(opt);
        lastGroup = opt.label;
      } else if (opt.group && opt.group !== lastGroup) {
        items.push({ isHeader: true, label: opt.group, value: `__header_${opt.group}` });
        lastGroup = opt.group;
        items.push(opt);
      } else {
        items.push(opt);
      }
    }
    return items;
  }, [filtered, search]);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={containerRef} className={cn("relative", className)}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={cn(
          "flex h-12 w-full items-center justify-between rounded-md border px-3 py-2 text-sm ring-offset-background",
          "bg-white/5 border-white/10 text-white hover:bg-white/10 transition-colors",
          !selectedOption && "text-slate-500",
          triggerClassName
        )}
        data-testid={testId}
      >
        <span className="truncate">{selectedOption ? selectedOption.label : placeholder}</span>
        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-md border border-white/10 bg-[#112240] shadow-xl animate-in fade-in-0 zoom-in-95">
          <div className="flex items-center border-b border-white/10 px-3">
            <Search className="mr-2 h-4 w-4 shrink-0 text-slate-500" />
            <input
              ref={inputRef}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={searchPlaceholder}
              className="flex h-10 w-full bg-transparent py-3 text-sm text-white outline-none placeholder:text-slate-500"
              data-testid={testId ? `${testId}-search` : undefined}
            />
          </div>
          <div className="max-h-60 overflow-y-auto p-1">
            {displayItems.length === 0 && (
              <div className="py-6 text-center text-sm text-slate-500">
                No results found
              </div>
            )}
            {displayItems.map((option) => {
              if (option.isHeader) {
                return (
                  <div key={option.value} className="px-3 py-2 text-xs font-bold uppercase tracking-wider text-slate-500 border-b border-white/5 mt-1">
                    {option.label}
                  </div>
                );
              }
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => {
                    onValueChange(option.value);
                    setOpen(false);
                    setSearch('');
                  }}
                  className={cn(
                    "relative flex w-full cursor-pointer select-none items-center rounded-sm px-3 py-2 text-sm text-white outline-none transition-colors",
                    "hover:bg-white/10",
                    value === option.value && "bg-cyan-500/20 text-cyan-400"
                  )}
                >
                  <Check className={cn("mr-2 h-4 w-4 flex-shrink-0", value === option.value ? "opacity-100 text-cyan-400" : "opacity-0")} />
                  <span className="truncate">{option.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
